import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface AdoptFromFosterModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onConfirm: (device: Device) => void | Promise<void>;
}

/**
 * Confirms moving a fostered pet straight into adoption, with the current foster
 * household as the adopter. Recalling the foster and creating the adoption
 * transfer both happen on confirm (see `handleAdoptFromFoster`).
 *
 * The adopter's email/phone are shown for review because this flow reuses the
 * foster's stored contact info without the editable form the manual adopter
 * transfer has — this is the rescue's chance to catch a stale address.
 */
export default function AdoptFromFosterModal({ open, onClose, device, onConfirm }: AdoptFromFosterModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const { holderName, id, petName, chip, holderEmail, holderPhone } = device;
  const petLabel = petName || 'this pet';

  async function handleConfirm() {
    // `device` is non-null here (guarded above) but re-checked to satisfy the
    // async closure. Guard against a double-submit while the transfer is in
    // flight — the modal stays open until it resolves, then closes.
    if (!device || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(device);
    } finally {
      setSubmitting(false);
      onClose();
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Move foster to adoption</ModalTitle>
      <ModalSubtitle>
        Move {petLabel} from foster to adoption with <strong>{holderName}</strong> as the adopter? The foster will be
        recalled and {holderName} will receive an email to set up their own Fi subscription.
        {chip?.id
          ? ` The pet's microchip${chip.serialNumber ? ` (${chip.serialNumber})` : ''} will transfer to the adopter automatically.`
          : ''}
      </ModalSubtitle>

      <dl className={styles.confirmDetails}>
        <div>
          <dt>Adopter</dt>
          <dd>{holderName}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{holderEmail || '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{holderPhone || '—'}</dd>
        </div>
      </dl>

      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Moving…' : 'Move to Adoption'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
